/**
 * GateEditor component - Single quality gate editor
 * Provides structured instruction format with 3 textareas (Evaluate, PASS when, FAIL when)
 * Enforces system gate protection and role toggling
 */

import {
  ArrowDown,
  ArrowUp,
  Lock,
  Skull,
  Shield,
  Trash2,
} from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { ConfirmDeleteModal } from '@/components/ui/ConfirmDeleteModal';
import { Input } from '@/components/ui/Input';
import type { Gate, GateRole } from '@/types/template';

// ============================================================================
// TYPES
// ============================================================================

export interface GateEditorProps {
  gate: Gate;
  index: number;
  totalGates: number;
  onUpdate: (updatedGate: Gate) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

// ============================================================================
// INSTRUCTION PARSE / FORMAT HELPERS
// ============================================================================

interface ParsedInstructions {
  evaluate: string;
  passWhen: string;
  failWhen: string;
}

/**
 * Parse a combined instructions string into its 3 parts.
 * Handles both "PASS when:" and "PASS when" marker formats.
 */
export function parseInstructions(instructions: string): ParsedInstructions {
  const evaluateMatch = instructions.match(/Evaluate:\s*([\s\S]*?)(?=PASS when)/i);
  const passMatch = instructions.match(/PASS when:\s*([\s\S]*?)(?=FAIL when)/i);
  const failMatch = instructions.match(/FAIL when:\s*([\s\S]*?)$/i);
  return {
    evaluate: evaluateMatch?.[1]?.trim() || '',
    passWhen: passMatch?.[1]?.trim() || '',
    failWhen: failMatch?.[1]?.trim() || '',
  };
}

/**
 * Combine 3 instruction parts into a single formatted string.
 */
export function formatInstructions(
  evaluate: string,
  passWhen: string,
  failWhen: string
): string {
  return `Evaluate: ${evaluate}\nPASS when: ${passWhen}\nFAIL when: ${failWhen}`;
}

// ============================================================================
// TEXTAREA CLASSES (matches existing codebase textarea styling)
// ============================================================================

const textareaClasses =
  'flex w-full font-sans text-md text-primary placeholder:text-muted transition-all duration-base ease-standard focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 rounded-md border border-border-subtle bg-surface px-4 py-3 focus-visible:border-brand/55 focus-visible:ring-4 focus-visible:ring-brand/12 resize-none';

const readOnlyTextareaClasses =
  'flex w-full font-sans text-md text-secondary rounded-md border border-border-subtle bg-surface-subtle px-4 py-3 resize-none cursor-not-allowed opacity-70';

// ============================================================================
// COMPONENT
// ============================================================================

export function GateEditor({
  gate,
  index,
  totalGates,
  onUpdate,
  onRemove,
  onMoveUp,
  onMoveDown,
}: GateEditorProps) {
  // Parse existing instructions into structured parts
  const parsed = useMemo(() => parseInstructions(gate.instructions), [gate.instructions]);

  const [evaluate, setEvaluate] = useState(parsed.evaluate);
  const [passWhen, setPassWhen] = useState(parsed.passWhen);
  const [failWhen, setFailWhen] = useState(parsed.failWhen);

  // Media-type customization state
  const hasOverrides = !!(gate.videoInstructions || gate.imageInstructions);
  const [customizeByMedia, setCustomizeByMedia] = useState(hasOverrides);

  // Parse video/image override instructions
  const parsedVideo = useMemo(
    () => gate.videoInstructions ? parseInstructions(gate.videoInstructions) : null,
    [gate.videoInstructions],
  );
  const parsedImage = useMemo(
    () => gate.imageInstructions ? parseInstructions(gate.imageInstructions) : null,
    [gate.imageInstructions],
  );

  const [videoEvaluate, setVideoEvaluate] = useState(parsedVideo?.evaluate ?? '');
  const [videoPassWhen, setVideoPassWhen] = useState(parsedVideo?.passWhen ?? '');
  const [videoFailWhen, setVideoFailWhen] = useState(parsedVideo?.failWhen ?? '');
  const [imageEvaluate, setImageEvaluate] = useState(parsedImage?.evaluate ?? '');
  const [imagePassWhen, setImagePassWhen] = useState(parsedImage?.passWhen ?? '');
  const [imageFailWhen, setImageFailWhen] = useState(parsedImage?.failWhen ?? '');

  // Only reset local state when switching to a different gate (not on every prop change)
  const [lastGateId, setLastGateId] = useState(gate.gateId);
  if (gate.gateId !== lastGateId) {
    const newParsed = parseInstructions(gate.instructions);
    setEvaluate(newParsed.evaluate);
    setPassWhen(newParsed.passWhen);
    setFailWhen(newParsed.failWhen);

    const newHasOverrides = !!(gate.videoInstructions || gate.imageInstructions);
    setCustomizeByMedia(newHasOverrides);
    const newParsedVideo = gate.videoInstructions ? parseInstructions(gate.videoInstructions) : null;
    const newParsedImage = gate.imageInstructions ? parseInstructions(gate.imageInstructions) : null;
    setVideoEvaluate(newParsedVideo?.evaluate ?? '');
    setVideoPassWhen(newParsedVideo?.passWhen ?? '');
    setVideoFailWhen(newParsedVideo?.failWhen ?? '');
    setImageEvaluate(newParsedImage?.evaluate ?? '');
    setImagePassWhen(newParsedImage?.passWhen ?? '');
    setImageFailWhen(newParsedImage?.failWhen ?? '');

    setLastGateId(gate.gateId);
  }

  const handleInstructionChange = useCallback(
    (field: 'evaluate' | 'passWhen' | 'failWhen', value: string) => {
      let newEvaluate = evaluate;
      let newPassWhen = passWhen;
      let newFailWhen = failWhen;

      if (field === 'evaluate') {
        newEvaluate = value;
        setEvaluate(value);
      } else if (field === 'passWhen') {
        newPassWhen = value;
        setPassWhen(value);
      } else {
        newFailWhen = value;
        setFailWhen(value);
      }

      onUpdate({
        ...gate,
        instructions: formatInstructions(newEvaluate, newPassWhen, newFailWhen),
      });
    },
    [gate, evaluate, passWhen, failWhen, onUpdate]
  );

  const handleVideoInstructionChange = useCallback(
    (field: 'evaluate' | 'passWhen' | 'failWhen', value: string) => {
      let newEval = videoEvaluate;
      let newPass = videoPassWhen;
      let newFail = videoFailWhen;

      if (field === 'evaluate') { newEval = value; setVideoEvaluate(value); }
      else if (field === 'passWhen') { newPass = value; setVideoPassWhen(value); }
      else { newFail = value; setVideoFailWhen(value); }

      onUpdate({
        ...gate,
        videoInstructions: formatInstructions(newEval, newPass, newFail),
      });
    },
    [gate, videoEvaluate, videoPassWhen, videoFailWhen, onUpdate],
  );

  const handleImageInstructionChange = useCallback(
    (field: 'evaluate' | 'passWhen' | 'failWhen', value: string) => {
      let newEval = imageEvaluate;
      let newPass = imagePassWhen;
      let newFail = imageFailWhen;

      if (field === 'evaluate') { newEval = value; setImageEvaluate(value); }
      else if (field === 'passWhen') { newPass = value; setImagePassWhen(value); }
      else { newFail = value; setImageFailWhen(value); }

      onUpdate({
        ...gate,
        imageInstructions: formatInstructions(newEval, newPass, newFail),
      });
    },
    [gate, imageEvaluate, imagePassWhen, imageFailWhen, onUpdate],
  );

  const handleCustomizeToggle = useCallback(() => {
    if (customizeByMedia) {
      // Turning off: clear overrides
      setCustomizeByMedia(false);
      setVideoEvaluate('');
      setVideoPassWhen('');
      setVideoFailWhen('');
      setImageEvaluate('');
      setImagePassWhen('');
      setImageFailWhen('');
      onUpdate({
        ...gate,
        videoInstructions: undefined,
        imageInstructions: undefined,
      });
    } else {
      // Turning on: pre-populate with shared values
      setCustomizeByMedia(true);
      setVideoEvaluate(evaluate);
      setVideoPassWhen(passWhen);
      setVideoFailWhen(failWhen);
      setImageEvaluate(evaluate);
      setImagePassWhen(passWhen);
      setImageFailWhen(failWhen);
      onUpdate({
        ...gate,
        videoInstructions: formatInstructions(evaluate, passWhen, failWhen),
        imageInstructions: formatInstructions(evaluate, passWhen, failWhen),
      });
    }
  }, [customizeByMedia, gate, evaluate, passWhen, failWhen, onUpdate]);

  const handleNameChange = useCallback(
    (name: string) => {
      if (name.length <= 100) {
        onUpdate({ ...gate, name });
      }
    },
    [gate, onUpdate]
  );

  const handleRoleToggle = useCallback(() => {
    const newRole: GateRole = gate.role === 'critical' ? 'standard' : 'critical';
    onUpdate({ ...gate, role: newRole });
  }, [gate, onUpdate]);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleDeleteClick = useCallback(() => {
    if (gate.isSystem) return;
    setShowDeleteConfirm(true);
  }, [gate.isSystem]);

  const handleDeleteConfirm = useCallback(() => {
    setShowDeleteConfirm(false);
    onRemove();
  }, [onRemove]);

  const isCritical = gate.role === 'critical';
  const isFirst = index === 0;
  const isLast = index === totalGates - 1;

  return (
    <Card className="relative">
      {/* Header Row */}
      <div className="flex items-center gap-3 mb-4">
        {/* Gate number */}
        <span className="flex-shrink-0 w-7 h-7 rounded-full bg-surface-subtle flex items-center justify-center text-xs font-semibold text-secondary">
          {index + 1}
        </span>

        {/* Name input */}
        <div className="flex-1 min-w-0">
          {gate.isSystem ? (
            <div className="flex items-center gap-2">
              <Lock className="h-4 w-4 text-muted flex-shrink-0" />
              <span className="text-sm font-medium text-primary truncate">{gate.name}</span>
              <span className="text-xs text-muted" title="System gate -- cannot be modified">
                System
              </span>
            </div>
          ) : (
            <Input
              value={gate.name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="Gate name..."
              maxLength={100}
            />
          )}
        </div>

        {/* Role badge toggle */}
        <button
          type="button"
          onClick={handleRoleToggle}
          className={`flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-colors cursor-pointer ${
            isCritical
              ? 'bg-danger/10 text-danger hover:bg-danger/20'
              : 'bg-brand/10 text-brand hover:bg-brand/20'
          }`}
          title={
            isCritical
              ? 'Critical gate - failing triggers Kill verdict'
              : 'Standard gate - failing contributes to Fix verdict'
          }
        >
          {isCritical ? (
            <>
              <Skull className="h-3.5 w-3.5" />
              Critical
            </>
          ) : (
            <>
              <Shield className="h-3.5 w-3.5" />
              Standard
            </>
          )}
        </button>

        {/* Reorder buttons */}
        <div className="flex-shrink-0 flex items-center gap-0.5">
          <Button
            variant="ghost"
            size="sm"
            onClick={onMoveUp}
            disabled={isFirst}
            title="Move up"
            className="!px-2"
          >
            <ArrowUp className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onMoveDown}
            disabled={isLast}
            title="Move down"
            className="!px-2"
          >
            <ArrowDown className="h-4 w-4" />
          </Button>
        </div>

        {/* Delete button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleDeleteClick}
          disabled={gate.isSystem}
          title={gate.isSystem ? 'System gate -- cannot be deleted' : 'Delete gate'}
          className={`!px-2 ${!gate.isSystem ? 'hover:text-danger' : ''}`}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      {/* Instruction Section */}
      {gate.isSystem ? (
        /* System gate: read-only display */
        <div className="space-y-3">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted">Instructions</label>
            <div className={readOnlyTextareaClasses} style={{ whiteSpace: 'pre-wrap', minHeight: '4rem' }}>
              {gate.instructions}
            </div>
          </div>
        </div>
      ) : (
        /* Non-system gate: 3 editable textareas + media customize toggle */
        <div className="space-y-3">
          {/* Shared instructions (hidden when customizing by media type) */}
          {!customizeByMedia && (
            <>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-secondary">
                  Evaluate
                </label>
                <textarea
                  value={evaluate}
                  onChange={(e) => handleInstructionChange('evaluate', e.target.value)}
                  placeholder="What to evaluate..."
                  rows={2}
                  className={textareaClasses}
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-success">
                  PASS when
                </label>
                <textarea
                  value={passWhen}
                  onChange={(e) => handleInstructionChange('passWhen', e.target.value)}
                  placeholder="Specific criteria for passing..."
                  rows={2}
                  className={textareaClasses}
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-danger">
                  FAIL when
                </label>
                <textarea
                  value={failWhen}
                  onChange={(e) => handleInstructionChange('failWhen', e.target.value)}
                  placeholder="Specific criteria for failing..."
                  rows={2}
                  className={textareaClasses}
                />
              </div>
            </>
          )}

          {/* Customize by media type toggle */}
          <div className="pt-2 border-t border-border-subtle">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={customizeByMedia}
                onChange={handleCustomizeToggle}
                className="w-4 h-4 rounded border-border-default text-brand focus:ring-brand/50"
              />
              <span className="text-xs font-medium text-secondary">
                Customize by media type
              </span>
              <span className="text-[10px] text-muted">
                (override instructions for video vs. image)
              </span>
            </label>
          </div>

          {/* Video/Image override fields (shown when customizeByMedia is checked) */}
          {customizeByMedia && (
            <div className="space-y-4 pl-3 border-l-2 border-brand/20">
              {/* Video overrides */}
              <div className="space-y-2">
                <p className="text-xs font-semibold text-blue-600 dark:text-blue-400">Video Overrides</p>
                <div>
                  <label className="mb-1 block text-[10px] font-medium text-secondary">Evaluate (Video)</label>
                  <textarea
                    value={videoEvaluate}
                    onChange={(e) => handleVideoInstructionChange('evaluate', e.target.value)}
                    placeholder="What to evaluate in videos..."
                    rows={2}
                    className={textareaClasses}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-[10px] font-medium text-success">PASS when (Video)</label>
                  <textarea
                    value={videoPassWhen}
                    onChange={(e) => handleVideoInstructionChange('passWhen', e.target.value)}
                    placeholder="Video-specific pass criteria..."
                    rows={2}
                    className={textareaClasses}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-[10px] font-medium text-danger">FAIL when (Video)</label>
                  <textarea
                    value={videoFailWhen}
                    onChange={(e) => handleVideoInstructionChange('failWhen', e.target.value)}
                    placeholder="Video-specific fail criteria..."
                    rows={2}
                    className={textareaClasses}
                  />
                </div>
              </div>

              {/* Image overrides */}
              <div className="space-y-2">
                <p className="text-xs font-semibold text-purple-600 dark:text-purple-400">Image Overrides</p>
                <div>
                  <label className="mb-1 block text-[10px] font-medium text-secondary">Evaluate (Image)</label>
                  <textarea
                    value={imageEvaluate}
                    onChange={(e) => handleImageInstructionChange('evaluate', e.target.value)}
                    placeholder="What to evaluate in images..."
                    rows={2}
                    className={textareaClasses}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-[10px] font-medium text-success">PASS when (Image)</label>
                  <textarea
                    value={imagePassWhen}
                    onChange={(e) => handleImageInstructionChange('passWhen', e.target.value)}
                    placeholder="Image-specific pass criteria..."
                    rows={2}
                    className={textareaClasses}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-[10px] font-medium text-danger">FAIL when (Image)</label>
                  <textarea
                    value={imageFailWhen}
                    onChange={(e) => handleImageInstructionChange('failWhen', e.target.value)}
                    placeholder="Image-specific fail criteria..."
                    rows={2}
                    className={textareaClasses}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Delete Gate Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Gate"
        description="This gate will be removed from the template. This action cannot be undone."
        itemName={gate.name || 'Untitled'}
        confirmText="Delete Gate"
      />
    </Card>
  );
}
